import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { useAuth, usePermissions } from '@/hooks/useAuth'
import { useLaboratoryServicesDetailed } from '@/hooks/usePrimoUp'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loading } from '@/components/ui/loading'
import { List, Save, ArrowLeft, RefreshCw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { CompanyPhasesEditor } from '@/components/listino/CompanyPhasesEditor'
import { Id } from '../../../convex/_generated/dataModel'
import { getListinoPricingErrors, listinoPricingFromPrices, updateListinoPricing, type ListinoPricing } from '@/lib/listinoDiscount'
import {
  inferLegacyPricingMode,
  type PricingMode,
} from '../../../convex/lib/applicationType'
import {
  laboratoryTestLabel,
  parseLaboratoryTests,
  type LaboratoryTest,
} from '../../../convex/lib/laboratoryTests'

interface ListinoFormData extends ListinoPricing {
  profile_id: string
  /** Per listino di compagnia (admin) */
  company_id: string
  organization_id: string
  nome_prestazione: string
  nome_visualizzato: string
  descrizione: string
  pricing_mode: PricingMode
  is_active: boolean
  laboratory_id?: number
  laboratory_materials?: any
  laboratory_tests?: any
}

interface LaboratoryServiceDetailed {
  code: string
  id: number
  materials: Array<{ id: number; name: string }>
  name: string
  net_price: number
  slug: string
  tests: LaboratoryTest[]
}

export default function ListinoForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const initialOrganizationId = searchParams.get('organizationId') || ''
  const initialCompanyId = initialOrganizationId ? '' : searchParams.get('companyId') || ''
  const isEdit = !!id
  
  const { profile } = useAuth()
  const { hasPermission } = usePermissions()
  
  const [listinoScope, setListinoScope] = useState<'personal' | 'company' | 'organization'>(initialOrganizationId ? 'organization' : initialCompanyId ? 'company' : 'personal')

  const [formData, setFormData] = useState<ListinoFormData>({
    profile_id: '',
    company_id: initialCompanyId,
    organization_id: initialOrganizationId,
    nome_prestazione: '',
    nome_visualizzato: '',
    descrizione: '',
    ...listinoPricingFromPrices(0),
    pricing_mode: 'per_tooth',
    is_active: true,
    laboratory_id: undefined,
    laboratory_materials: undefined,
    laboratory_tests: undefined
  })
  
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Get listino item if editing
  const listinoItem = useQuery(
    api.listino.getListinoItem,
    isEdit ? { listino_id: id } : "skip"
  )

  // Check permissions and get users conditionally
  const canViewCompanyUsers = hasPermission('view_company_users')
  const users = useQuery(
    api.profiles.listProfiles,
    canViewCompanyUsers && profile
      ? {
          paginationOpts: { numItems: 100, cursor: null },
          viewerKey: profile._id,
        }
      : 'skip'
  )

  // Mutations
  const createListinoItem = useMutation(api.listino.createListinoItem)
  const createService = useMutation(api.listino.createService)
  const updateListinoItem = useMutation(api.listino.updateListinoItem)

  // PrimoUp detailed laboratory services (with materials and tests)
  const { 
    services: detailedServices, 
    isLoading: loadingDetailedServices, 
    error: detailedServicesError,
    refreshServices: refreshDetailedServices 
  } = useLaboratoryServicesDetailed()

  // State for selected PrimoUp service
  const [selectedPrimoUpService, setSelectedPrimoUpService] = useState<LaboratoryServiceDetailed | null>(null)
  
  // State for PrimoUp service search
  const [primoUpSearchTerm, setPrimoUpSearchTerm] = useState('')
  const [showPrimoUpDropdown, setShowPrimoUpDropdown] = useState(false)
  const [highlightedServiceIndex, setHighlightedServiceIndex] = useState(-1)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [highlightedUserIndex, setHighlightedUserIndex] = useState(-1)

  // Check permissions
  const canManageOrganizationListino = hasPermission('manage_listino') &&
    (profile?.ruolo === 'amministratore' || profile?.ruolo === 'tecnico_lab')
  const canManageListino = hasPermission('manage_listino') &&
    (!(listinoItem?.organization_id || initialOrganizationId) || canManageOrganizationListino)
  const organizations = useQuery(api.organizations.listOrganizations,
    hasPermission('view_companies') || hasPermission('view_company_data') || hasPermission('manage_companies') ? {} : 'skip')
  const canManagePhases =
    hasPermission('manage_company_phases') ||
    hasPermission('manage_phase_policies')
  const canListCompanies =
    hasPermission('view_companies') ||
    hasPermission('view_company_data') ||
    hasPermission('manage_companies')

  const companies = useQuery(
    api.companies.listCompanies,
    (canManageListino || canManagePhases) && canListCompanies
      ? {}
      : 'skip'
  )

  const companyRows = Array.isArray(companies)
    ? companies
    : companies?.page ?? []
  const activeClinic = companyRows.find((company) => company._id === profile?.company_id)
  const sharedOrganization = organizations?.find((organization) => organization._id === listinoItem?.organization_id)
  const organizationPhaseScope = Boolean(listinoItem?.organization_id) &&
    (profile?.ruolo === 'amministratore' || profile?.ruolo === 'tecnico_lab')
  const phaseCompanyId = listinoItem?.company_id || (
    listinoItem?.organization_id && sharedOrganization?.share_price_lists &&
    activeClinic?.organization_id === listinoItem.organization_id ? activeClinic._id : undefined
  )
  const phaseCompanyName = companyRows.find((company) => company._id === phaseCompanyId)?.nome
  const assignedClinic = companyRows.find((company) => company._id === formData.company_id)
  const assignedClinicOrganization = organizations?.find((organization) => organization._id === assignedClinic?.organization_id)
  const isLocalListSuspended = listinoScope !== 'organization' && Boolean(assignedClinicOrganization?.share_price_lists)

  // Filter users to show only clinic owners and dentists
  const eligibleUsers = users?.page?.filter((user: any) => 
    user.ruolo === 'clinic_owner' || user.ruolo === 'dentista'
  ) || []

  const selectedListinoUser = eligibleUsers.find(
    (user: any) => user._id === formData.profile_id
  )
  const selectedUserCompanyIds = Array.from(
    new Set(
      [
        ...(selectedListinoUser?.company_ids ?? []),
        selectedListinoUser?.company_id,
      ]
        .filter(Boolean)
        .map(String)
    )
  )
  const selectedUserCompanyRows = companyRows.filter((company: any) =>
    selectedUserCompanyIds.includes(String(company._id))
  )
  const selectedUserCompanyKey = selectedUserCompanyIds.join('|')

  const filteredEligibleUsers = eligibleUsers.filter((user: any) => {
    const fullName = `${user.nome} ${user.cognome}`.toLowerCase()
    return userSearchTerm.trim() === '' ||
      fullName.includes(userSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
  })

  const filteredPrimoUpServices = detailedServices.filter(service =>
    primoUpSearchTerm.trim() === '' ||
    service.name.toLowerCase().includes(primoUpSearchTerm.toLowerCase()) ||
    service.code.toLowerCase().includes(primoUpSearchTerm.toLowerCase())
  )

  const handleSelectFilteredService = (service: LaboratoryServiceDetailed) => {
    handlePrimoUpServiceSelect(service)
    setPrimoUpSearchTerm('')
    setShowPrimoUpDropdown(false)
    setHighlightedServiceIndex(-1)
  }

  // Load listino item data when editing
  useEffect(() => {
    if (isEdit && listinoItem) {
      setListinoScope(
        listinoItem.organization_id ? 'organization' : listinoItem.listino_visibility === 'personal' ? 'personal' : 'company'
      )

      setFormData({
        profile_id: listinoItem.profile_id || '',
        company_id: listinoItem.company_id || '',
        organization_id: listinoItem.organization_id || '',
        nome_prestazione: listinoItem.nome_prestazione || '',
        nome_visualizzato: listinoItem.nome_visualizzato || '',
        descrizione: listinoItem.descrizione || '',
        ...listinoPricingFromPrices(listinoItem.prezzo || 0, listinoItem.prezzo_scontato),
        pricing_mode:
          listinoItem.pricing_mode ?? inferLegacyPricingMode(listinoItem),
        is_active: listinoItem.is_active ?? true,
        laboratory_id: listinoItem.laboratory_id,
        laboratory_materials: listinoItem.laboratory_materials,
        laboratory_tests: listinoItem.laboratory_tests
      })
    }
  }, [isEdit, listinoItem])

  // A catalog refresh must not overwrite the user's price or discount edits.
  useEffect(() => {
    if (formData.laboratory_id && detailedServices.length > 0) {
      const service = detailedServices.find(s => s.id === formData.laboratory_id)
      if (service) setSelectedPrimoUpService(service)
    }
  }, [formData.laboratory_id, detailedServices])

  useEffect(() => {
    if (!showPrimoUpDropdown || filteredPrimoUpServices.length === 0) {
      setHighlightedServiceIndex(-1)
    } else if (highlightedServiceIndex >= filteredPrimoUpServices.length) {
      setHighlightedServiceIndex(0)
    }
  }, [showPrimoUpDropdown, filteredPrimoUpServices.length, highlightedServiceIndex])

  useEffect(() => {
    if (!showUserDropdown || filteredEligibleUsers.length === 0) {
      setHighlightedUserIndex(-1)
    } else if (highlightedUserIndex >= filteredEligibleUsers.length) {
      setHighlightedUserIndex(0)
    }
  }, [showUserDropdown, filteredEligibleUsers.length, highlightedUserIndex])

  useEffect(() => {
    if (!showPrimoUpDropdown || highlightedServiceIndex < 0) return

    const highlightedElement = document.getElementById(`primoup-service-option-${highlightedServiceIndex}`)
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedServiceIndex, showPrimoUpDropdown])

  useEffect(() => {
    if (!showUserDropdown || highlightedUserIndex < 0) return

    const highlightedElement = document.getElementById(`listino-user-option-${highlightedUserIndex}`)
    if (highlightedElement) {
      highlightedElement.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedUserIndex, showUserDropdown])

  useEffect(() => {
    if (!formData.profile_id) return

    const selectedUser = eligibleUsers.find((u: any) => u._id === formData.profile_id)
    if (selectedUser) {
      const label = `${selectedUser.nome} ${selectedUser.cognome} (${selectedUser.ruolo})`
      if (userSearchTerm !== label) {
        setUserSearchTerm(label)
      }
    }
  }, [formData.profile_id, eligibleUsers, userSearchTerm])

  useEffect(() => {
    if (!selectedListinoUser || selectedUserCompanyIds.length === 0) return
    setFormData((current) => {
      if (selectedUserCompanyIds.includes(String(current.company_id))) {
        return current
      }
      const preferredCompanyId = [
        selectedListinoUser.active_company_id,
        selectedListinoUser.company_id,
        selectedUserCompanyIds[0],
      ].find((companyId) =>
        companyId && selectedUserCompanyIds.includes(String(companyId))
      )
      return {
        ...current,
        company_id: preferredCompanyId ? String(preferredCompanyId) : '',
      }
    })
  }, [formData.profile_id, selectedUserCompanyKey])

  if (!canManageListino && !canManagePhases) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-8 text-center">
            <List className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Accesso Negato</h2>
            <p className="text-gray-600">Non hai i permessi per gestire il listino.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isEdit && listinoItem === undefined) {
    return <Loading />
  }

  if (!canManageListino && canManagePhases) {
    if (!isEdit || !id || (!phaseCompanyId && !organizationPhaseScope)) {
      return <div className="p-8">Seleziona una clinica attiva per configurare le fasi della prestazione.</div>
    }
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/listino" className="text-sm text-primary hover:underline">
          ← Torna al listino
        </Link>
        <CompanyPhasesEditor
          key={`${id}:${organizationPhaseScope ? 'organization' : phaseCompanyId}`}
          companyId={phaseCompanyId as Id<'companies'> | undefined}
          companyName={phaseCompanyName}
          organizationId={listinoItem.organization_id}
          organizationName={sharedOrganization?.nome}
          organizationScope={organizationPhaseScope}
          listinoId={id as Id<'listino'>}
          laboratoryTests={listinoItem.laboratory_tests}
        />
      </div>
    )
  }

  if (canViewCompanyUsers && (users === undefined || users.page === undefined)) {
    return <Loading />
  }

  const handleInputChange = (field: keyof ListinoFormData, value: string | number | boolean) => {
    setFormData(prev => {
      if (field === 'prezzo') return updateListinoPricing(prev, { prezzo: Number(value) })
      if (field === 'sconto_percentuale') return updateListinoPricing(prev, { sconto_percentuale: String(value) })
      if (field === 'prezzo_netto') return updateListinoPricing(prev, { prezzo_netto: String(value) })
      return { ...prev, [field]: value }
    })
    // Clear error when user starts typing
    if (field === 'prezzo' || field === 'sconto_percentuale' || field === 'prezzo_netto') {
      setErrors(prev => ({ ...prev, prezzo: '', sconto_percentuale: '', prezzo_netto: '' }))
    } else if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const laboratoryTests = parseLaboratoryTests(formData.laboratory_tests, {
    preserveDisplayNameWhitespace: true,
  })

  const handleTestDisplayNameChange = (testId: number, value: string) => {
    setFormData((previous) => ({
      ...previous,
      laboratory_tests: parseLaboratoryTests(previous.laboratory_tests, {
        preserveDisplayNameWhitespace: true,
      }).map((test) =>
        test.id === testId ? { ...test, display_name: value } : test
      ),
    }))
    if (errors.laboratory_tests) {
      setErrors((previous) => ({ ...previous, laboratory_tests: '' }))
    }
  }

  const handlePrimoUpServiceSelect = (service: LaboratoryServiceDetailed) => {
    setSelectedPrimoUpService(service)
    setFormData(prev => ({
      ...updateListinoPricing(prev, { prezzo: service.net_price }),
      nome_prestazione: service.name,
      nome_visualizzato: service.name,
      prezzo: service.net_price,
      pricing_mode: inferLegacyPricingMode({ nome_prestazione: service.name }),
      laboratory_id: service.id,
      laboratory_materials: service.materials,
      laboratory_tests: service.tests
    }))
  }

  const clearPrimoUpService = () => {
    setSelectedPrimoUpService(null)
    setFormData(prev => ({
      ...prev,
      laboratory_id: undefined,
      laboratory_materials: undefined,
      laboratory_tests: undefined
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = getListinoPricingErrors(formData)

    if (!isEdit && listinoScope === 'personal' && !formData.profile_id) {
      newErrors.profile_id = 'Utente è obbligatorio'
    }
    if (listinoScope === 'personal' && !formData.company_id) {
      newErrors.company_id = 'Clinica obbligatoria per il listino personale'
    }
    if (!isEdit && listinoScope === 'company' && !formData.company_id) {
      newErrors.company_id = 'Clinica obbligatoria per il listino'
    }

    if (listinoScope === 'organization' && !formData.organization_id) {
      newErrors.organization_id = 'Seleziona la società del listino'
    }

    if (!formData.nome_prestazione.trim()) {
      newErrors.nome_prestazione = 'Nome prestazione è obbligatorio'
    }

    const tests = parseLaboratoryTests(formData.laboratory_tests, {
      preserveDisplayNameWhitespace: true,
    })
    const invalidLocalName = tests.find(
      (test) => (test.display_name?.length ?? 0) > 100
    )
    if (invalidLocalName) {
      newErrors.laboratory_tests = 'Il nome locale di ogni prova può contenere al massimo 100 caratteri.'
    } else {
      const labels = tests.map((test) =>
        laboratoryTestLabel(test).toLocaleLowerCase('it-IT')
      )
      if (new Set(labels).size !== labels.length) {
        newErrors.laboratory_tests = 'Usa un nome locale diverso per ogni prova.'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    
    try {
      const itemData = {
        nome_prestazione: formData.nome_prestazione.trim(),
        nome_visualizzato: formData.nome_visualizzato.trim() || undefined,
        descrizione: formData.descrizione.trim() || undefined,
        prezzo: formData.prezzo,
        prezzo_scontato: isEdit || formData.prezzo_scontato > 0 ? formData.prezzo_scontato : undefined,
        pricing_mode: formData.pricing_mode,
        is_active: formData.is_active,
        laboratory_id: formData.laboratory_id,
        laboratory_materials: formData.laboratory_materials,
        laboratory_tests: formData.laboratory_tests
          ? parseLaboratoryTests(formData.laboratory_tests)
          : undefined
      }

      if (isEdit) {
        await updateListinoItem({
          listino_id: id!,
          ...itemData,
          ...(listinoScope !== 'organization' ? { company_id: formData.company_id as Id<'companies'> } : {}),
          ...(listinoScope === 'personal'
            ? { profile_id: formData.profile_id as Id<'profiles'> }
            : {}),
        })
      } else if ((listinoScope === 'company' || listinoScope === 'organization') && canManageListino) {
        await createService({
          nome_prestazione: itemData.nome_prestazione,
          nome_visualizzato: itemData.nome_visualizzato,
          descrizione: itemData.descrizione,
          prezzo: itemData.prezzo,
          prezzo_scontato: itemData.prezzo_scontato,
          pricing_mode: itemData.pricing_mode,
          ...(listinoScope === 'organization'
            ? { organization_id: formData.organization_id as Id<'organizations'> }
            : { company_id: formData.company_id as Id<'companies'> }),
          listino_visibility: listinoScope,
          laboratory_id: itemData.laboratory_id,
          laboratory_materials: itemData.laboratory_materials,
          laboratory_tests: itemData.laboratory_tests,
        })
      } else {
        await createListinoItem({
          ...itemData,
          profile_id: formData.profile_id as Id<'profiles'>,
          company_id: formData.company_id as Id<'companies'>,
        })
      }

      navigate(listinoScope === 'organization' ? `/listino?organizationId=${formData.organization_id}` : '/listino')
    } catch (error) {
      console.error('Errore salvataggio elemento listino:', error)
      alert('Errore durante il salvataggio dell\'elemento del listino')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <Link to="/listino">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold flex items-center space-x-2">
            <List className="w-8 h-8" />
            <span>{isEdit ? 'Modifica Prestazione' : 'Nuova Prestazione'}</span>
          </h1>
          <p className="text-gray-600 mt-2">
            {isEdit ? 'Modifica i dati della prestazione' : 'Inserisci i dati della nuova prestazione'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Dati Prestazione</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {isEdit && listinoItem && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                <p className="font-medium text-gray-900">
                  {listinoScope === 'organization' ? 'Listino della società' : listinoScope === 'company' ? 'Listino della clinica' : 'Listino personale (medico)'}
                </p>
                {listinoScope === 'organization' ? (
                  <p className="mt-1 text-gray-600">
                    Società: {organizations?.find((organization) => organization._id === listinoItem.organization_id)?.nome ?? '—'}
                  </p>
                ) : listinoScope === 'company' ? (
                  <p className="mt-1 text-gray-600">
                    Clinica:{' '}
                    {companyRows.find((c: any) => c._id === listinoItem.company_id)
                      ?.nome ?? '—'}
                  </p>
                ) : (
                  <p className="mt-1 text-gray-600">
                    Medico:{' '}
                    {(() => {
                      const u = users?.page?.find(
                        (x: any) => x._id === listinoItem.profile_id
                      )
                      return u ? `${u.nome} ${u.cognome}` : '—'
                    })()}
                  </p>
                )}
              </div>
            )}

            {!isEdit && canManageListino && canListCompanies && (
              <div>
                <span className="mb-2 block text-sm font-medium">Ambito voce</span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={listinoScope === 'personal' ? 'default' : 'outline'}
                    aria-pressed={listinoScope === 'personal'}
                    size="sm"
                    onClick={() => {
                      setListinoScope('personal')
                      setFormData((p) => ({ ...p, company_id: '' }))
                    }}
                  >
                    Personale (medico)
                  </Button>
                  <Button
                    type="button"
                    variant={listinoScope === 'company' ? 'default' : 'outline'}
                    aria-pressed={listinoScope === 'company'}
                    size="sm"
                    onClick={() => {
                      setListinoScope('company')
                      setFormData((p) => ({ ...p, profile_id: '' }))
                      setUserSearchTerm('')
                    }}
                  >
                    Clinica (tutti i medici)
                  </Button>
                  {canManageOrganizationListino && (
                    <Button type="button" size="sm" aria-pressed={listinoScope === 'organization'} variant={listinoScope === 'organization' ? 'default' : 'outline'} onClick={() => {
                      setListinoScope('organization')
                      setFormData((current) => ({ ...current, profile_id: '', company_id: '' }))
                    }}>Società (più cliniche)</Button>
                  )}
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  Scegli a chi assegnare la prestazione. Con la condivisione attiva, le cliniche usano esclusivamente il listino della società; quelli propri e personali restano salvati e sospesi.
                </p>
              </div>
            )}

            {!isEdit && listinoScope === 'organization' && canManageOrganizationListino && (
              <div>
                <label htmlFor="listino-organization" className="mb-2 block text-sm font-medium">Società *</label>
                <select id="listino-organization" value={formData.organization_id}
                  onChange={(event) => handleInputChange('organization_id', event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  aria-invalid={!!errors.organization_id} aria-describedby="listino-organization-hint">
                  <option value="">Seleziona società</option>
                  {organizations?.map((organization) => <option key={organization._id} value={organization._id}>{organization.nome}</option>)}
                </select>
                <p id="listino-organization-hint" className="mt-2 text-sm text-gray-600">
                  {organizations?.find((organization) => organization._id === formData.organization_id)?.share_price_lists
                    ? 'Condivisione attiva: le voci attive di questo listino sostituiscono i listini propri e personali in tutte le cliniche collegate.'
                    : 'Condivisione disattivata: puoi preparare il listino e attivarlo dalle impostazioni della società.'}
                </p>
                {errors.organization_id && <p className="mt-1 text-sm text-red-700" role="alert">{errors.organization_id}</p>}
              </div>
            )}

            {/* Clinica (solo nuova voce di studio) */}
            {!isEdit && listinoScope === 'company' && canListCompanies && (
              <div>
                <label className="mb-2 block text-sm font-medium">
                  Clinica *
                </label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.company_id}
                  onChange={(e) =>
                    handleInputChange('company_id', e.target.value)
                  }
                >
                  <option value="">Seleziona clinica</option>
                  {companyRows.map((c: any) => (
                    <option key={c._id} value={c._id}>
                      {c.nome}
                    </option>
                  ))}
                </select>
                {errors.company_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.company_id}</p>
                )}
              </div>
            )}

            {/* Utente (solo listino personale in creazione) */}
            {!isEdit && listinoScope === 'personal' && (
            <div className="space-y-5">
              <div>
              <label className="block text-sm font-medium mb-2">
                Utente *
              </label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Cerca e seleziona utente"
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value)
                    setShowUserDropdown(true)
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  onBlur={() => setTimeout(() => setShowUserDropdown(false), 120)}
                  onKeyDown={(e) => {
                    if (!showUserDropdown || filteredEligibleUsers.length === 0) {
                      if (e.key === 'ArrowDown' && filteredEligibleUsers.length > 0) {
                        e.preventDefault()
                        setShowUserDropdown(true)
                        setHighlightedUserIndex(0)
                      }
                      return
                    }

                    if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      setHighlightedUserIndex((prev) => prev < filteredEligibleUsers.length - 1 ? prev + 1 : 0)
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      setHighlightedUserIndex((prev) => prev > 0 ? prev - 1 : filteredEligibleUsers.length - 1)
                    } else if (e.key === 'Enter') {
                      if (highlightedUserIndex >= 0 && highlightedUserIndex < filteredEligibleUsers.length) {
                        e.preventDefault()
                        const user = filteredEligibleUsers[highlightedUserIndex]
                        handleInputChange('profile_id', user._id)
                        setUserSearchTerm(`${user.nome} ${user.cognome} (${user.ruolo})`)
                        setShowUserDropdown(false)
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      setShowUserDropdown(false)
                      setHighlightedUserIndex(-1)
                    }
                  }}
                  className={errors.profile_id ? 'border-red-500' : ''}
                />
                {showUserDropdown && (
                  <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {filteredEligibleUsers.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">Nessun utente trovato</div>
                    ) : (
                      filteredEligibleUsers.map((user: any, index: number) => (
                        <button
                          id={`listino-user-option-${index}`}
                          key={user._id}
                          type="button"
                          className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 ${highlightedUserIndex === index ? 'bg-blue-50' : 'hover:bg-gray-100'}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setHighlightedUserIndex(index)}
                          onClick={() => {
                            handleInputChange('profile_id', user._id)
                            setUserSearchTerm(`${user.nome} ${user.cognome} (${user.ruolo})`)
                            setShowUserDropdown(false)
                          }}
                        >
                          {user.nome} {user.cognome} ({user.ruolo})
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              {errors.profile_id && (
                <p className="text-red-500 text-sm mt-1">{errors.profile_id}</p>
              )}
              </div>

              {formData.profile_id && (
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Clinica del listino *
                  </label>
                  {selectedUserCompanyRows.length > 0 ? (
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                      value={formData.company_id}
                      onChange={(event) =>
                        handleInputChange('company_id', event.target.value)
                      }
                    >
                      {selectedUserCompanyRows.map((company: any) => (
                        <option key={company._id} value={company._id}>
                          {company.nome}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Il medico selezionato non ha cliniche disponibili.
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    La voce appartiene al medico in questa clinica. Si applica quando la clinica usa il proprio listino.
                  </p>
                  {errors.company_id && (
                    <p className="mt-1 text-sm text-red-500">{errors.company_id}</p>
                  )}
                </div>
              )}
            </div>
            )}

            {isLocalListSuspended && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
                <p className="font-semibold">Sospeso: listino società in uso</p>
                <p className="mt-1 leading-6">
                  {assignedClinic?.nome} usa esclusivamente il listino di {assignedClinicOrganization?.nome}.
                  Puoi salvare questa voce, ma non sarà disponibile per nuove prescrizioni finché la condivisione resta attiva.
                  Disattivandola, le voci proprie e personali attive tornano disponibili.
                </p>
              </div>
            )}

            {/* PrimoUp Services Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Servizio PrimoUp (Opzionale)
              </label>
              <div className="space-y-3">
                {selectedPrimoUpService ? (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900">{selectedPrimoUpService.name}</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Codice: {selectedPrimoUpService.code} | Prezzo: €{selectedPrimoUpService.net_price}
                        </p>
                        {selectedPrimoUpService.materials.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-blue-800">Materiali:</p>
                            <p className="text-xs text-blue-700">
                              {selectedPrimoUpService.materials.map(m => m.name).join(', ')}
                            </p>
                          </div>
                        )}
                        {selectedPrimoUpService.tests.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-blue-800">Test:</p>
                            <p className="text-xs text-blue-700">
                              {selectedPrimoUpService.tests.map(t => t.name).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearPrimoUpService}
                        className="ml-3"
                      >
                        Rimuovi
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder={loadingDetailedServices ? 'Caricamento servizi...' : 'Scrivi per cercare e selezionare un servizio PrimoUp'}
                        value={primoUpSearchTerm}
                        onChange={(e) => {
                          setPrimoUpSearchTerm(e.target.value)
                          setShowPrimoUpDropdown(true)
                        }}
                        onFocus={() => setShowPrimoUpDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowPrimoUpDropdown(false), 120)
                        }}
                        onKeyDown={(e) => {
                          if (!showPrimoUpDropdown || filteredPrimoUpServices.length === 0) {
                            if (e.key === 'ArrowDown' && filteredPrimoUpServices.length > 0) {
                              e.preventDefault()
                              setShowPrimoUpDropdown(true)
                              setHighlightedServiceIndex(0)
                            }
                            return
                          }

                          if (e.key === 'ArrowDown') {
                            e.preventDefault()
                            setHighlightedServiceIndex((prev) => {
                              const next = prev < filteredPrimoUpServices.length - 1 ? prev + 1 : 0
                              return next
                            })
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault()
                            setHighlightedServiceIndex((prev) => {
                              const next = prev > 0 ? prev - 1 : filteredPrimoUpServices.length - 1
                              return next
                            })
                          } else if (e.key === 'Enter') {
                            if (highlightedServiceIndex >= 0 && highlightedServiceIndex < filteredPrimoUpServices.length) {
                              e.preventDefault()
                              handleSelectFilteredService(filteredPrimoUpServices[highlightedServiceIndex])
                            }
                          } else if (e.key === 'Escape') {
                            e.preventDefault()
                            setShowPrimoUpDropdown(false)
                            setHighlightedServiceIndex(-1)
                          }
                        }}
                        className="w-full"
                        disabled={loadingDetailedServices}
                      />
                      {primoUpSearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setPrimoUpSearchTerm('')
                            setShowPrimoUpDropdown(true)
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          x
                        </button>
                      )}

                      {showPrimoUpDropdown && !loadingDetailedServices && (
                        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                          {filteredPrimoUpServices.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">Nessun servizio trovato</div>
                          ) : (
                            filteredPrimoUpServices.map((service, index) => (
                              <button
                                id={`primoup-service-option-${index}`}
                                key={service.id}
                                type="button"
                                className={`w-full px-3 py-2 text-left text-sm border-b last:border-b-0 ${
                                  highlightedServiceIndex === index
                                    ? 'bg-blue-50'
                                    : 'hover:bg-gray-100'
                                }`}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setHighlightedServiceIndex(index)}
                                onClick={() => {
                                  handleSelectFilteredService(service)
                                }}
                              >
                                <div className="font-medium text-gray-900">{service.name}</div>
                                <div className="text-xs text-gray-500">{service.code} - EUR {service.net_price}</div>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>

                    {detailedServicesError && (
                      <p className="text-red-500 text-sm mt-1">
                        Errore caricamento servizi: {detailedServicesError}
                      </p>
                    )}
                    <div className="flex justify-between items-center">
                      <p className="text-xs text-gray-500">
                        {detailedServices.length} servizi disponibili
                        {primoUpSearchTerm && ` (${filteredPrimoUpServices.length} filtrati)`}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { void refreshDetailedServices().catch(() => {}) }}
                        disabled={loadingDetailedServices}
                      >
                        <RefreshCw className={`w-4 h-4 ${loadingDetailedServices ? 'animate-spin' : ''}`} />
                        Aggiorna
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {laboratoryTests.length > 0 && (
              <section
                aria-labelledby="local-test-names-title"
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="mb-4">
                  <h2 id="local-test-names-title" className="text-sm font-semibold text-gray-900">
                    Nomi delle prove in PrimoFastLab
                  </h2>
                  <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-600">
                    Personalizza soltanto il nome mostrato nel portale. Il nome originale e
                    l&apos;ID PrimoUp restano invariati e continuano a essere usati per la sincronizzazione.
                  </p>
                </div>

                <div className="space-y-3">
                  {laboratoryTests.map((test) => (
                    <div
                      key={test.id}
                      className="grid gap-2 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:items-center md:gap-4"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{test.name}</p>
                        <p className="mt-0.5 text-xs text-gray-500">Nome originale PrimoUp · ID {test.id}</p>
                      </div>
                      <div>
                        <label
                          htmlFor={`test-display-name-${test.id}`}
                          className="mb-1 block text-xs font-medium text-gray-700"
                        >
                          Nome mostrato nel portale
                        </label>
                        <Input
                          id={`test-display-name-${test.id}`}
                          value={test.display_name ?? ''}
                          onChange={(event) =>
                            handleTestDisplayNameChange(test.id, event.target.value)
                          }
                          maxLength={100}
                          placeholder={test.name}
                          aria-describedby={`test-display-name-help-${test.id}`}
                        />
                        <p id={`test-display-name-help-${test.id}`} className="mt-1 text-xs text-gray-500">
                          Lascia vuoto per mostrare “{test.name}”.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {errors.laboratory_tests && (
                  <p role="alert" className="mt-3 text-sm text-red-600">
                    {errors.laboratory_tests}
                  </p>
                )}
              </section>
            )}

            <div>
              <label htmlFor="listino-service-name" className="mb-2 block text-sm font-medium">Nome prestazione *</label>
              <Input id="listino-service-name" value={formData.nome_prestazione}
                onChange={(event) => handleInputChange('nome_prestazione', event.target.value)}
                readOnly={formData.laboratory_id !== undefined} required
                aria-invalid={!!errors.nome_prestazione}
                aria-describedby={errors.nome_prestazione ? 'listino-service-name-error' : undefined} />
              {errors.nome_prestazione && <p id="listino-service-name-error" role="alert" className="mt-1 text-sm text-red-700">{errors.nome_prestazione}</p>}
            </div>

            {/* Nome Visualizzato */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Nome Visualizzato
              </label>
              <Input
                value={formData.nome_visualizzato}
                onChange={(e) => handleInputChange('nome_visualizzato', e.target.value)}
                placeholder="Nome personalizzato per la visualizzazione"
              />
              <p className="text-xs text-gray-500 mt-1">
                Se vuoto, verrà usato il nome della prestazione
              </p>
            </div>

            {/* Descrizione */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Descrizione
              </label>
              <textarea
                value={formData.descrizione}
                onChange={(e) => handleInputChange('descrizione', e.target.value)}
                placeholder="Descrizione dettagliata della prestazione"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Prezzo e Sconto */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Unità di calcolo *
              </label>
              <select
                value={formData.pricing_mode}
                onChange={(e) =>
                  handleInputChange('pricing_mode', e.target.value as PricingMode)
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="per_tooth">Per dente</option>
                <option value="per_arch">Per arcata</option>
                <option value="per_mouth">Per bocca</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                “Per dente” moltiplica il prezzo per tutti gli elementi selezionati,
                anche quando usi il pulsante Arcata. “Per arcata” applica il prezzo
                una volta per ogni arcata completa. “Per bocca” applica il prezzo
                una sola volta all&apos;intera prescrizione.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Prezzo di listino (€) *
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.prezzo || ''}
                  onChange={(e) => handleInputChange('prezzo', Number(e.target.value) || 0)}
                  placeholder="0.00"
                  className={errors.prezzo ? 'border-red-500' : ''}
                />
                {errors.prezzo && (
                  <p className="text-red-500 text-sm mt-1">{errors.prezzo}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="listino-discount" className="block text-sm font-medium mb-2">
                  Sconto (%)
                </label>
                <Input
                  id="listino-discount"
                  type="text"
                  inputMode="decimal"
                  value={formData.sconto_percentuale}
                  onChange={(e) => handleInputChange('sconto_percentuale', e.target.value)}
                  placeholder="0"
                  aria-invalid={!!errors.sconto_percentuale}
                  aria-describedby={errors.sconto_percentuale ? 'listino-discount-error' : undefined}
                  className={errors.sconto_percentuale ? 'border-red-500' : ''}
                />
                {errors.sconto_percentuale && (
                  <p id="listino-discount-error" className="text-red-500 text-sm mt-1">{errors.sconto_percentuale}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="listino-net-price" className="block text-sm font-medium mb-2">
                  Prezzo Netto (€)
                </label>
                <Input
                  id="listino-net-price"
                  type="text"
                  inputMode="decimal"
                  value={formData.prezzo_netto}
                  onChange={(e) => handleInputChange('prezzo_netto', e.target.value)}
                  placeholder="0,00"
                  aria-invalid={!!errors.prezzo_netto}
                  aria-describedby={errors.prezzo_netto ? 'listino-net-price-hint listino-net-price-error' : 'listino-net-price-hint'}
                  className={errors.prezzo_netto ? 'border-red-500' : ''}
                />
                <p id="listino-net-price-hint" className="text-xs text-gray-500 mt-1">
                  {formData.prezzo_scontato > formData.prezzo
                    ? 'Il netto supera il listino: chi consulta la prestazione vedrà solo il prezzo netto.'
                    : 'Modifica il netto o lo sconto. Il prezzo netto può anche superare il listino.'}
                </p>
                {errors.prezzo_netto && (
                  <p id="listino-net-price-error" className="text-red-500 text-sm mt-1">{errors.prezzo_netto}</p>
                )}
              </div>
            </div>

            {/* Stato */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">{isLocalListSuspended ? 'Prestazione abilitata nel listino proprio' : 'Prestazione attiva'}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {isLocalListSuspended
                  ? 'Questa impostazione sarà applicata quando la clinica tornerà a usare il proprio listino.'
                  : 'Le prestazioni inattive non sono selezionabili per nuove prescrizioni.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Link to="/listino">
                <Button variant="outline" type="button">
                  Annulla
                </Button>
              </Link>
              <Button type="submit" disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvataggio...' : (isEdit ? 'Aggiorna' : 'Crea Prestazione')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {isEdit &&
        id &&
        (phaseCompanyId || organizationPhaseScope) &&
        canManagePhases && (
          <CompanyPhasesEditor
            key={`${id}:${organizationPhaseScope ? 'organization' : phaseCompanyId}`}
            companyId={phaseCompanyId as Id<'companies'> | undefined}
            companyName={phaseCompanyName}
            organizationId={listinoItem.organization_id}
            organizationName={sharedOrganization?.nome}
            organizationScope={organizationPhaseScope}
            listinoId={id as Id<'listino'>}
            laboratoryTests={listinoItem.laboratory_tests}
          />
        )}
    </div>
  )
}
